import { api } from '../../../shared/api/client';
import {
  type ListEnvelope,
  normalizeListEnvelope,
  toGenexusParams,
} from '../../../shared/api/genexus-query';
import type { DataTableQuery } from '../../../shared/components/DataTable';
import type {
  CompanyNotaFiscalCancelarInput,
  CompanyNotaFiscalDto,
  CompanyNotaFiscalEvento,
  CompanyNotaFiscalGerarAutomaticaInput,
  CompanyNotaFiscalGerarManualInput,
  CompanyNotaFiscalListItem,
  CompanyNotasFiscaisListExtraParams,
  RelatorioExtratoParams,
} from './types';

/**
 * Sub-recurso NF Empresa aninhado em uma Empresa. `companyId` fica fechado
 * no escopo. NF Empresa não tem create/update/delete pela API REST — o ciclo
 * de vida é controlado pelas actions custom (`gerar-*`, `cancelar*`).
 */
const ALL_BASE = '/v1/companies/notas-fiscais';

/**
 * Lista de NF de todas as emitentes — `ApiEmpresaNotaFiscalListAll`
 * (ATD-0068 / card [yZ21KuqH]). `empresaId` fica de fora; a tela manda o
 * filtro só quando o usuário escolhe uma empresa pela rota aninhada.
 */
export async function listAllCompanyNotasFiscais(
  query: DataTableQuery,
  extra: CompanyNotasFiscaisListExtraParams = {},
): Promise<ListEnvelope<CompanyNotaFiscalListItem>> {
  const params = { ...toGenexusParams(query), ...extra };
  const { data } = await api.get<{
    data: CompanyNotaFiscalListItem[];
    meta: { total: number; pagina?: number; porPagina?: number };
  }>(ALL_BASE, { params });
  return normalizeListEnvelope(data);
}

export function createCompanyNotasFiscaisApi(
  companyId: number,
  extra: CompanyNotasFiscaisListExtraParams = {},
): {
  list: (query: DataTableQuery) => Promise<ListEnvelope<CompanyNotaFiscalListItem>>;
  get: (id: string) => Promise<CompanyNotaFiscalDto>;
  gerarAutomatica: (input: CompanyNotaFiscalGerarAutomaticaInput) => Promise<CompanyNotaFiscalDto>;
  gerarManual: (input: CompanyNotaFiscalGerarManualInput) => Promise<CompanyNotaFiscalDto>;
  cancelar: (id: string, input: CompanyNotaFiscalCancelarInput) => Promise<CompanyNotaFiscalDto>;
  uploadFile: (id: string, file: File) => Promise<CompanyNotaFiscalDto>;
  downloadFile: (id: string) => Promise<Blob>;
  relatorioExtrato: (params: RelatorioExtratoParams) => Promise<Blob>;
  listEventos: (id: string) => Promise<CompanyNotaFiscalEvento[]>;
  reenviar: (id: string) => Promise<CompanyNotaFiscalDto>;
} {
  const BASE = `/v1/companies/${companyId}/notas-fiscais`;

  return {
    async list(query: DataTableQuery): Promise<ListEnvelope<CompanyNotaFiscalListItem>> {
      const params = { ...toGenexusParams(query), ...extra };
      const { data } = await api.get<{
        data: CompanyNotaFiscalListItem[];
        meta: { total: number; pagina?: number; porPagina?: number };
      }>(BASE, { params });
      return normalizeListEnvelope(data);
    },

    async get(id: string): Promise<CompanyNotaFiscalDto> {
      const { data } = await api.get<{ data: CompanyNotaFiscalDto }>(`${BASE}/${id}`);
      return data.data;
    },

    async gerarAutomatica(input): Promise<CompanyNotaFiscalDto> {
      const { data } = await api.post<{ data: CompanyNotaFiscalDto }>(
        `${BASE}/gerar-automatica`,
        input,
      );
      return data.data;
    },

    async gerarManual(input): Promise<CompanyNotaFiscalDto> {
      const { data } = await api.post<{ data: CompanyNotaFiscalDto }>(
        `${BASE}/gerar-manual`,
        input,
      );
      return data.data;
    },

    async cancelar(id, input): Promise<CompanyNotaFiscalDto> {
      const { data } = await api.post<{ data: CompanyNotaFiscalDto }>(
        `${BASE}/${id}/cancelar`,
        input,
      );
      return data.data;
    },

    /**
     * Upload via multipart/form-data (campo `file`). BFF valida magic-bytes
     * `%PDF-` e teto 10 MB antes de reencaixar em JSON+base64 pro upstream.
     */
    async uploadFile(id, file): Promise<CompanyNotaFiscalDto> {
      const fd = new FormData();
      fd.append('file', file, file.name);
      const { data } = await api.post<{ data: CompanyNotaFiscalDto }>(`${BASE}/${id}/file`, fd);
      return data.data;
    },

    /** Baixa o PDF como Blob — UI abre via `URL.createObjectURL`. */
    async downloadFile(id): Promise<Blob> {
      const res = await api.get<Blob>(`${BASE}/${id}/file`, { responseType: 'blob' });
      return res.data;
    },

    /**
     * Relatório-extrato Excel mensal (card [wSlANOgS]/ATD-0047). O BFF chama o
     * upstream (que devolve JSON `{arquivo: base64, ...}`), decodifica e faz o
     * stream do `.xlsx` — o front recebe o Blob direto.
     */
    async relatorioExtrato(params: RelatorioExtratoParams): Promise<Blob> {
      const res = await api.get<Blob>(`${BASE}/relatorio-extrato`, {
        params,
        responseType: 'blob',
      });
      return res.data;
    },

    /**
     * Histórico de eventos de integração fiscal (card [BqANQSEU]/ATD-0032).
     * Backend retorna ordenado por `dataHora DESC`.
     */
    async listEventos(id): Promise<CompanyNotaFiscalEvento[]> {
      const { data } = await api.get<{ data: CompanyNotaFiscalEvento[] }>(`${BASE}/${id}/eventos`);
      return data.data;
    },

    /**
     * Recoloca NF na fila do IntegraNotas — card [zKY9EItf]/ATD-0033.
     * Backend valida: só `status=PEM` + `statusEnvio=ERR` → flipa pra `PEN`,
     * limpa chaveNF/numero/RPS/pdf, registra evento `REENVIO`.
     */
    async reenviar(id): Promise<CompanyNotaFiscalDto> {
      const { data } = await api.post<{ data: CompanyNotaFiscalDto }>(`${BASE}/${id}/reenviar`, {});
      return data.data;
    },
  };
}

export type CompanyNotasFiscaisApi = ReturnType<typeof createCompanyNotasFiscaisApi>;
